import React, { useEffect } from 'react'
import { useDispatch, batch } from 'react-redux'

import PlaceHolder from './PlaceHolder'
import Image from './Image'
import { ICON_MAPPING, TEXT_MAPPING, TEXT_MAPPING_PLURAL } from './Icons'

import { range } from '../utils'
import { useIsMobile, usePagination, setInfoPanel } from '../slices/ui'
import { setMapPin, setMapView } from '../slices/map'

import { LOAD_MORE } from '../trans'

const AmenityItem = (props) => {
  const { amenity, amenitiesType } = props || {}
  const fallbackImage = ICON_MAPPING[props.amenitiesType]({})
  const dispatch = useDispatch()
  const isMobile = useIsMobile()
  const setPin = () => dispatch(setMapPin(amenity.lnglat))
  const resetPin = () => dispatch(setMapPin(null))
  const onClick = () => {
    if (!amenitiesType) return
    batch(() => {
      dispatch(setMapView({ lnglat: amenity.lnglat, zoom: 16 }))
      dispatch(setInfoPanel({
        type: amenity.source,
        id: amenity.id
      }))
    })
  }

  return (
    <div className="pwa__infopanel__amenity fade-in delay button basic"
      onMouseEnter={(isMobile && amenity.lnglat) ? undefined : setPin}
      onMouseLeave={(isMobile && amenity.lnglat) ? undefined : resetPin}
      onClick={onClick}>
      <div className="pwa__infopanel__amenity__image">
        <Image src={amenity.image && amenity.image.small} fallback={fallbackImage}/>
      </div>
      <div className="pwa__infopanel__amenity__content">
        <h4><PlaceHolder value={amenity.title}/></h4>
        <i><PlaceHolder value={amenity.subtitle}/></i>
      </div>
    </div>
  )
}

const AmenitiesPage = (props) => {
  const { useAmenities, amenitiesType, count, page } = props
  const dispatch = useDispatch()
  const [currentPage, setPage] = usePagination('page' + amenitiesType)
  const query = useAmenities({ amenitiesType: amenitiesType, page: page })
  const { objects, has_next } = query || {}
  const amenities = objects || new Array(count <= 8 ? count : 5).fill({})
  const loadMore = has_next && currentPage === page
  return (
    <>
      {
        amenities.map(
          (amenity, index) => <AmenityItem amenity={amenity}
                                           amenitiesType={amenitiesType}
                                           page={page}
                                           key={index} />
        )
      }
      { loadMore && (
        <div className="pwa__infopanel__amenity__loadmore">
          <button className="button"
            onClick={() => dispatch(setPage(page + 1))}>
            { LOAD_MORE }
          </button>
        </div>
      )}
    </>
  )
}

const AmenitiesList = (props) => {
  const { useAmenities, amenitiesType, count } = props
  const dispatch = useDispatch()
  const [page, setPage] = usePagination('page' + amenitiesType)
  const text = count > 1 ? TEXT_MAPPING_PLURAL : TEXT_MAPPING
  const maxPage = count ? Math.ceil(count / 5) + (count % 5 > 3 ? 1 : 0) : 0

  useEffect(() => {
    if (page > maxPage) {
      dispatch(setPage(maxPage))
    }
  }, [page, maxPage, setPage, dispatch])

  if (page > maxPage) return null

  return (
    <section className="pwa__infopanel__amenity__group">
      <h3 className="pwa__infopanel__amenity__title">
        { text[amenitiesType] }
      </h3>
      {
        range(1, page).map(
          (page) => <AmenitiesPage useAmenities={useAmenities}
                                   amenitiesType={amenitiesType}
                                   count={count - ((page - 1) * 5)}
                                   page={page}
                                   key={page} />
        )
      }
    </section>
  )
}

const Amenities = React.memo((props) => {
  const { amenities, useAmenities } = props
  const amenitiesList = []

  if (!amenities) return null

  for (const [amenitiesType, count] of Object.entries(amenities)) {
    if (amenitiesType in ICON_MAPPING) {
      amenitiesList.push(
        <AmenitiesList useAmenities={useAmenities}
                       amenitiesType={amenitiesType}
                       count={count}
                       key={amenitiesType}/>
      )
    }
  }

  return amenitiesList
})

Amenities.displayName = 'Amenities'

export default Amenities
